import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import type { ExamAttendanceSheetData } from '../../../services/examAttendanceSheetService';
import {
  Page,
  pdfColors, pdfFontSize, pdfLetter, pdfSpacing, fmtClassName, fmtMarks,
  type SchoolBranding, type OfficialDetail,
} from '../../../utils/pdf';

interface Props {
  sheet: ExamAttendanceSheetData;
  /** Typed in on the page at print time. */
  room?: string;
  invigilators: string[];
  /** YYYY-MM-DD. */
  examDate?: string;
  branding: SchoolBranding;
  generatedAt: string;
}

const GRID = pdfColors.dividerStrong;
const CELL = { borderRightWidth: 0.6, borderRightColor: GRID, borderRightStyle: 'solid' } as const;

const styles = StyleSheet.create({
  extras: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 6 },
  extra: { fontSize: 8, color: pdfColors.textSecondary, marginLeft: 14 },
  extraKey: { color: pdfColors.textMuted },
  extraValue: { fontWeight: 600, color: pdfColors.textPrimary },
  head: { flexDirection: 'row', backgroundColor: pdfColors.textPrimary, borderTopLeftRadius: 3, borderTopRightRadius: 3 },
  th: {
    paddingVertical: 6, paddingHorizontal: 6, fontSize: 6.8, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: pdfLetter.openLabel, color: pdfColors.textOnDark, textAlign: 'center',
  },
  grid: { borderLeftWidth: 0.6, borderLeftColor: GRID, borderLeftStyle: 'solid' },
  row: { flexDirection: 'row', minHeight: 22, borderBottomWidth: 0.6, borderBottomColor: GRID, borderBottomStyle: 'solid' },
  td: { ...CELL, paddingHorizontal: 6, justifyContent: 'center', fontSize: 8.5, color: pdfColors.textPrimary },
  muted: { color: pdfColors.textMuted },
  strong: { fontWeight: 600 },
  close: { marginTop: pdfSpacing.sm, borderWidth: 0.6, borderColor: GRID, borderStyle: 'solid', borderRadius: 4 },
  tally: { flexDirection: 'row', backgroundColor: pdfColors.surfaceSoft, borderBottomWidth: 0.6, borderBottomColor: pdfColors.divider, borderBottomStyle: 'solid' },
  tallyCell: { flex: 1, flexDirection: 'row', alignItems: 'flex-end', paddingVertical: 6, paddingHorizontal: 12 },
  tallyRule: { borderRightWidth: 0.6, borderRightColor: pdfColors.divider, borderRightStyle: 'solid' },
  label: { fontSize: pdfFontSize.xxs, fontWeight: 700, textTransform: 'uppercase', letterSpacing: pdfLetter.openLabel, color: pdfColors.textMuted, marginRight: 8 },
  blank: { flex: 1, borderBottomWidth: 0.6, borderBottomColor: pdfColors.textFaint, borderBottomStyle: 'dotted', fontSize: 9, fontWeight: 700, paddingHorizontal: 4 },
  signs: { flexDirection: 'row' },
  sign: { flex: 1, paddingTop: 8, paddingBottom: 7, paddingHorizontal: 14 },
  fields: { flexDirection: 'row', marginTop: 22 },
  field: { borderTopWidth: 0.7, borderTopColor: pdfColors.textSecondary, borderTopStyle: 'solid', paddingTop: 3, fontSize: 7, fontWeight: 600, color: pdfColors.textMuted },
  empty: {
    marginTop: pdfSpacing.lg, paddingVertical: 36, textAlign: 'center', fontSize: 9, color: pdfColors.textMuted,
    borderWidth: 0.6, borderColor: GRID, borderStyle: 'dashed', borderRadius: 4,
  },
});

const longDate = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const SignSlot: React.FC<{ title: string; rule?: boolean }> = ({ title, rule }) => (
  <View style={rule ? [styles.sign, styles.tallyRule] : styles.sign}>
    <Text style={styles.label}>{title}</Text>
    <View style={styles.fields}>
      <Text style={[styles.field, { flex: 2, marginRight: 14 }]}>Signature</Text>
      <Text style={[styles.field, { flex: 1 }]}>Date</Text>
    </View>
  </View>
);

/**
 * The exam-hall signature sheet for one paper: the school's letterhead,
 * the room and invigilators, every enrolled student with a box to sign and
 * one for remarks, then the hall's tally and the invigilator's and HOD's
 * signatures.
 */
const ExamAttendanceSheetDocument: React.FC<Props> = ({ sheet, room, invigilators, examDate, branding, generatedAt }) => {
  const cls = fmtClassName(sheet.class.name);
  const wholeClass = !sheet.section;
  const date = examDate ? longDate(examDate) : null;
  const details: OfficialDetail[] = [
    // Labelled like Max marks: "Class: 9 Sci", or "Class: 9" for the whole class.
    { label: 'Class', value: [sheet.class.name.trim(), sheet.section?.name].filter(Boolean).join(' ') },
    { label: 'Max marks', value: fmtMarks(sheet.subject.maxMarks) },
    ...(date ? [{ value: date }] : []),
  ];
  const invigilatorTitle = invigilators.length > 1 ? 'Invigilators' : 'Invigilator';
  const cols = [
    { key: 'n', header: 'S.No', width: 34 },
    { key: 'roll', header: 'Roll', width: 46 },
    { key: 'name', header: 'Student', flex: 1, left: true },
    ...(wholeClass ? [{ key: 'section', header: 'Section', width: 58 }] : []),
    { key: 'sign', header: 'Signature', width: 136 },
    { key: 'remarks', header: 'Remarks', width: 92 },
  ];
  const size = (c: typeof cols[number]) => ('width' in c && c.width ? { width: c.width } : { flex: 1 });

  return (
    <Document title={`Attendance Sheet — ${sheet.exam.name} — ${sheet.subject.name}`} author={branding.name}>
      <Page
        branding={branding}
        meta={{
          title: `Attendance Sheet — ${sheet.exam.name} — ${sheet.subject.name}`,
          subtitle: [sheet.section ? `${cls} · ${sheet.section.name}` : cls, date].filter(Boolean).join('  ·  '),
          generatedAt,
          signed: true,
        }}
        official={{ details }}
      >
        {(room || invigilators.length > 0) && (
          <View style={styles.extras}>
            {room ? <Text style={styles.extra}><Text style={styles.extraKey}>Room  </Text><Text style={styles.extraValue}>{room}</Text></Text> : null}
            {invigilators.length > 0 ? (
              <Text style={styles.extra}><Text style={styles.extraKey}>{invigilatorTitle}  </Text><Text style={styles.extraValue}>{invigilators.join(', ')}</Text></Text>
            ) : null}
          </View>
        )}

        {sheet.students.length === 0 ? (
          <Text style={styles.empty}>No enrolled students found for this exam and subject.</Text>
        ) : (
          <>
            <View style={styles.head} fixed>
              {cols.map(c => <Text key={c.key} style={[styles.th, size(c), c.left ? { textAlign: 'left' } : {}]}>{c.header}</Text>)}
            </View>
            <View style={styles.grid}>
              {sheet.students.map((s, i) => (
                <View key={s.studentId} style={styles.row} wrap={false}>
                  <View style={[styles.td, { width: 34, alignItems: 'center' }]}><Text style={styles.muted}>{i + 1}</Text></View>
                  <View style={[styles.td, { width: 46, alignItems: 'center' }]}><Text style={styles.strong}>{s.rollNumber}</Text></View>
                  <View style={[styles.td, { flex: 1 }]}><Text style={styles.strong}>{s.name}</Text></View>
                  {wholeClass && <View style={[styles.td, { width: 58 }]}><Text style={styles.muted}>{s.sectionName ?? '—'}</Text></View>}
                  <View style={[styles.td, { width: 136 }]} />
                  <View style={[styles.td, { width: 92 }]} />
                </View>
              ))}
            </View>

            <View style={styles.close} wrap={false}>
              <View style={styles.tally}>
                <View style={[styles.tallyCell, styles.tallyRule]}>
                  <Text style={styles.label}>Total</Text>
                  <Text style={styles.blank}>{sheet.students.length}</Text>
                </View>
                <View style={[styles.tallyCell, styles.tallyRule]}>
                  <Text style={styles.label}>Present</Text>
                  <Text style={styles.blank}> </Text>
                </View>
                <View style={styles.tallyCell}>
                  <Text style={styles.label}>Absent</Text>
                  <Text style={styles.blank}> </Text>
                </View>
              </View>
              <View style={styles.signs}>
                <SignSlot title={invigilatorTitle} rule />
                <SignSlot title="Checked by (HOD / Exam Controller)" />
              </View>
            </View>
          </>
        )}
      </Page>
    </Document>
  );
};

export default ExamAttendanceSheetDocument;
